import { useEffect, useRef } from 'react';

const COLORS = ['#e86e64', '#e7bd55', '#58b39f', '#4c8cb5', '#8b70c9', '#df85a7', '#78a95b'];
const MAX_TRAIL_PARTICLES = 240;
const PARTICLE_SIZE = 8;
const PARTICLE_SPACING = 12;

function randomIndex(maximum: number) {
  const value = new Uint32Array(1);
  crypto.getRandomValues(value);
  return value[0] % maximum;
}

export function BeadParticleField() {
  const fieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const field = fieldRef.current;
    const host = field?.parentElement;
    if (!field || !host || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let lastX: number | null = null;
    let lastY: number | null = null;
    let distanceCarry = 0;
    let trail: HTMLElement[] = [];

    const removeTrail = () => {
      field.querySelectorAll('.bead-pointer-particle').forEach((bead) => bead.remove());
      trail = [];
    };

    const createParticle = (x: number, y: number) => {
      if (trail.length >= MAX_TRAIL_PARTICLES) trail.shift()?.remove();
      const bead = document.createElement('i');
      bead.className = 'bead-pointer-particle';
      bead.style.setProperty('--bead-x', `${x - PARTICLE_SIZE / 2}px`);
      bead.style.setProperty('--bead-y', `${y - PARTICLE_SIZE / 2}px`);
      bead.style.setProperty('--bead-color', COLORS[randomIndex(COLORS.length)]);
      bead.style.setProperty('--bead-drift', `${randomIndex(25) - 12}px`);
      field.append(bead);
      trail.push(bead);
      const remove = () => {
        bead.remove();
        const index = trail.indexOf(bead);
        if (index >= 0) trail.splice(index, 1);
      };
      bead.addEventListener('animationend', remove, { once: true });
      window.setTimeout(remove, 1800);
    };

    const emitPoint = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const bounds = host.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) return;
      const x = event.clientX - bounds.left;
      const y = event.clientY - bounds.top;
      if (lastX === null || lastY === null) {
        lastX = x;
        lastY = y;
        distanceCarry = 0;
        createParticle(x, y);
        return;
      }
      const deltaX = x - lastX;
      const deltaY = y - lastY;
      const distance = Math.hypot(deltaX, deltaY);
      if (distance <= Number.EPSILON) return;
      let nextDistance = PARTICLE_SPACING - distanceCarry;
      while (nextDistance <= distance) {
        const ratio = nextDistance / distance;
        createParticle(lastX + deltaX * ratio, lastY + deltaY * ratio);
        nextDistance += PARTICLE_SPACING;
      }
      distanceCarry = (distanceCarry + distance) % PARTICLE_SPACING;
      lastX = x;
      lastY = y;
    };

    const emit = (event: PointerEvent) => {
      const coalesced = event.getCoalescedEvents?.() || [];
      const points = coalesced.length ? coalesced : [event];
      points.forEach(emitPoint);
    };
    const enter = (event: PointerEvent) => {
      lastX = null;
      lastY = null;
      distanceCarry = 0;
      emitPoint(event);
    };
    const leave = () => {
      lastX = null;
      lastY = null;
      distanceCarry = 0;
    };

    host.addEventListener('pointerenter', enter, { passive: true });
    host.addEventListener('pointermove', emit, { passive: true });
    host.addEventListener('pointerleave', leave, { passive: true });
    return () => {
      removeTrail();
      host.removeEventListener('pointerenter', enter);
      host.removeEventListener('pointermove', emit);
      host.removeEventListener('pointerleave', leave);
    };
  }, []);

  return <div ref={fieldRef} className="bead-particle-field" aria-hidden="true">
    {Array.from({ length: 14 }, (_, index) => <i className="bead-star" key={index} />)}
  </div>;
}
